/**
 * Human-only ephemeral Team Chat (ADR-004, CR-R39–R44).
 * - Lives ONLY in this component's React state: never localStorage/sessionStorage/IndexedDB/Cache/service worker.
 * - Cleared on project switch (component is keyed by project), unmount, logout, page hide/reload.
 * - Max 200 messages; each line disappears 30 s after it arrived on this screen (REALTIME_POLICY.teamClientTtlMs).
 * - No offline queue: if the socket is not open the message is NOT sent, and the user is told so.
 * - Separate input from the AI composer; no model selector; @mentions are plain text.
 * - Files/photos: relayed from server memory, fetched once into an in-memory blob, shown for the same 30 s,
 *   then the blob URL is revoked. Photos are re-encoded first (location/EXIF removed). Non-images are never
 *   rendered — download only, as application/octet-stream.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import type { ServerEvent } from '@converoom/contracts';
import { REALTIME_POLICY, TEAM_DROP_BLOCKED_EXT } from '@converoom/contracts';
import type { Realtime, ConnState } from '../lib/realtime';
import type { Me } from '../lib/types';
import { time } from '../lib/format';
import { api, ApiError } from '../lib/api';
import { canUseLiveCamera, formatSize, prepareImage } from '../lib/media';
import { EmojiButton, insertAt } from './EmojiPicker';
import { CameraCapture } from './CameraCapture';

interface FilePart {
  kind: 'image' | 'file';
  name: string;
  size: number;
  state: 'loading' | 'ready' | 'failed';
  url?: string;
}

interface Line {
  key: string;
  from: string;
  mine: boolean;
  text: string;
  at: number;
  /** Local receive time; the 30 s lifetime counts from here (immune to clock skew between devices). */
  receivedAt: number;
  file?: FilePart;
}

const SEND_ERROR: Record<string, string> = {
  rate_limited: 'Çok hızlı gönderiyorsunuz; biraz bekleyip tekrar deneyin.',
  not_connected: 'Bağlantı yok — dosya gönderilmedi.',
  file_type_blocked: 'Bu dosya türü güvenlik nedeniyle gönderilemez (program, betik, HTML, SVG…).',
  too_large: 'Dosya çok büyük (en fazla 10 MB).',
  FST_REQ_FILE_TOO_LARGE: 'Dosya çok büyük (en fazla 10 MB).',
  busy: 'Sunucu şu an çok dosya taşıyor; birkaç saniye sonra tekrar deneyin.',
  empty_file: 'Dosya boş.',
};

function blockedLocally(name: string) {
  const ext = name.toLowerCase().split('.').pop() ?? '';
  return name.includes('.') && (TEAM_DROP_BLOCKED_EXT as readonly string[]).includes(ext);
}

export function TeamChat({
  projectId,
  rt,
  conn,
  me,
}: {
  projectId: string;
  rt: Realtime;
  conn: ConnState;
  me: Me;
}) {
  const [lines, setLines] = useState<Line[]>([]);
  const [text, setText] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [sending, setSending] = useState(0);
  const [camera, setCamera] = useState(false);
  const [viewing, setViewing] = useState<{ key: string; url: string; name: string } | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const galleryInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  /** Every blob URL this component created, so none outlives its line. */
  const urls = useRef(new Set<string>());

  const revoke = useCallback((u?: string) => {
    if (!u) return;
    URL.revokeObjectURL(u);
    urls.current.delete(u);
  }, []);

  useEffect(() => {
    let alive = true;
    const fetchDrop = async (key: string, dropId: string, kind: 'image' | 'file', mime: string) => {
      let state: FilePart['state'] = 'failed';
      let url: string | undefined;
      try {
        const res = await fetch(`/v1/team-drops/${dropId}`, {
          credentials: 'same-origin',
          cache: 'no-store',
        });
        if (res.ok) {
          const raw = await res.blob();
          // Only server-recognised raster images get an image type; everything else stays inert.
          const blob = new Blob([raw], { type: kind === 'image' ? mime : 'application/octet-stream' });
          url = URL.createObjectURL(blob);
          urls.current.add(url);
          state = 'ready';
        }
      } catch {
        /* network error → failed */
      }
      if (!alive) return revoke(url);
      setLines((prev) => {
        if (!prev.some((l) => l.key === key)) {
          revoke(url); // the line already expired while downloading
          return prev;
        }
        return prev.map((l) => (l.key === key && l.file ? { ...l, file: { ...l.file, state, url } } : l));
      });
    };

    const off = rt.on((e: ServerEvent) => {
      if (e.t === 'team.message' && e.projectId === projectId) {
        setLines((prev) =>
          [
            ...prev,
            {
              key: `${e.fromUserId}:${e.tempId}:${e.at}`,
              from: e.fromName,
              mine: e.fromUserId === me.id,
              text: e.text,
              at: Date.parse(e.at),
              receivedAt: Date.now(),
            },
          ].slice(-REALTIME_POLICY.teamClientMaxMessages),
        );
      } else if (e.t === 'team.file' && e.projectId === projectId) {
        const key = `${e.fromUserId}:${e.tempId}:${e.at}:${e.dropId}`;
        setLines((prev) =>
          [
            ...prev,
            {
              key,
              from: e.fromName,
              mine: e.fromUserId === me.id,
              text: '',
              at: Date.parse(e.at),
              receivedAt: Date.now(),
              file: { kind: e.kind, name: e.name, size: e.size, state: 'loading' as const },
            },
          ].slice(-REALTIME_POLICY.teamClientMaxMessages),
        );
        void fetchDrop(key, e.dropId, e.kind, e.mime);
      } else if (e.t === 'team.rejected' && e.projectId === projectId) {
        setNote(
          e.reason === 'rate_limited'
            ? 'Çok hızlı gönderiyorsunuz; mesaj iletilmedi.'
            : e.reason === 'size'
              ? 'Mesaj çok uzun (en fazla 2000 karakter).'
              : 'Mesaj iletilmedi: bu projede yetkiniz yok.',
        );
      } else if (e.t === 'access.revoked' && e.projectId === projectId && !e.roomId) {
        setLines([]);
      }
    });
    const clear = () => setLines([]);
    window.addEventListener('pagehide', clear);
    const ttl = setInterval(() => {
      setNow(Date.now());
      setLines((prev) => {
        const keep = prev.filter((l) => Date.now() - l.receivedAt < REALTIME_POLICY.teamClientTtlMs);
        if (keep.length === prev.length) return prev;
        for (const l of prev) if (!keep.includes(l)) revoke(l.file?.url);
        return keep;
      });
    }, 1_000);
    const allUrls = urls.current;
    return () => {
      alive = false;
      off();
      window.removeEventListener('pagehide', clear);
      clearInterval(ttl);
      setLines([]);
      for (const u of allUrls) URL.revokeObjectURL(u);
      allUrls.clear();
    };
  }, [projectId, rt, me.id, revoke]);

  // A cleared page (pagehide/revocation) must not leave blob URLs behind.
  useEffect(() => {
    const live = new Set(lines.map((l) => l.file?.url).filter(Boolean));
    for (const u of [...urls.current]) if (!live.has(u)) revoke(u);
    if (viewing && !lines.some((l) => l.key === viewing.key)) setViewing(null);
  }, [lines, viewing, revoke]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [lines.length]);

  function submit(e: FormEvent) {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setNote(null);
    const ok = rt.send({ t: 'team.send', projectId, text: t, tempId: crypto.randomUUID() });
    if (!ok) {
      setNote('Bağlantı yok — mesaj gönderilmedi ve sonradan da gönderilmeyecek.');
      return;
    }
    setText('');
  }

  async function sendFile(original: File, asPhoto: boolean) {
    setNote(null);
    if (conn !== 'open') return setNote('Bağlantı yok — dosya gönderilmedi.');
    if (blockedLocally(original.name)) return setNote(SEND_ERROR.file_type_blocked!);
    setSending((n) => n + 1);
    try {
      const f = asPhoto || original.type.startsWith('image/') ? await prepareImage(original) : original;
      if (f.size > REALTIME_POLICY.teamDropMaxBytes) return setNote(SEND_ERROR.too_large!);
      await api.upload(`/v1/projects/${projectId}/team-drops?tempId=${crypto.randomUUID()}`, f);
    } catch (err) {
      const code = err instanceof ApiError ? (err.status === 413 ? 'too_large' : err.code) : 'network';
      setNote(SEND_ERROR[code] ?? `Gönderilemedi (${code}).`);
    } finally {
      setSending((n) => n - 1);
    }
  }

  const pickFiles = (list: FileList | null, asPhoto: boolean) => {
    for (const f of [...(list ?? [])].slice(0, 5)) void sendFile(f, asPhoto);
  };

  const left = (l: Line) =>
    Math.max(0, Math.ceil((REALTIME_POLICY.teamClientTtlMs - (now - l.receivedAt)) / 1000));
  const offline = conn !== 'open';

  return (
    <section className="team" aria-labelledby="team-title">
      <div className="team-label" id="team-title">
        Ekip Sohbeti
        <div className="hint" style={{ fontWeight: 400 }}>
          Yalnızca ekip · AI kapalı · Kalıcı geçmiş yok · Mesajlar 30 sn sonra silinir
        </div>
      </div>
      <div
        className="team-log"
        ref={logRef}
        role="log"
        aria-live="polite"
        aria-label="Ekip sohbeti mesajları"
        data-testid="team-log"
      >
        {lines.length === 0 && (
          <p className="hint">
            Mesajlar, dosyalar ve fotoğraflar yalnızca şu an bağlı olan ekip üyelerine iletilir; kaydedilmez,
            30 saniye sonra herkesin ekranından silinir ve sonradan gelenler göremez.
          </p>
        )}
        {lines.map((l) => (
          <div key={l.key}>
            <span className="who">{l.mine ? 'Siz' : l.from}</span>
            <span className="hint">{time(new Date(l.at).toISOString())}</span>
            {l.file ? (
              <div className="team-file" data-testid="team-file">
                {l.file.state === 'loading' && <span className="hint">{l.file.name} alınıyor…</span>}
                {l.file.state === 'failed' && (
                  <span className="error-text">
                    {l.file.name} alınamadı (süresi doldu ya da bağlantı koptu).
                  </span>
                )}
                {l.file.state === 'ready' && l.file.kind === 'image' && l.file.url && (
                  <button
                    type="button"
                    className="team-photo"
                    onClick={() => setViewing({ key: l.key, url: l.file!.url!, name: l.file!.name })}
                    aria-label={`Fotoğrafı büyüt: ${l.file.name}`}
                  >
                    <img
                      src={l.file.url}
                      alt={`${l.from} gönderdi: ${l.file.name}`}
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                    />
                  </button>
                )}
                {l.file.state === 'ready' && l.file.kind === 'file' && (
                  <a className="team-dl" href={l.file.url} download={l.file.name} rel="noopener">
                    📄 {l.file.name}
                    <span className="hint"> · {formatSize(l.file.size)} · indir</span>
                  </a>
                )}
                <span className="hint ttl">{left(l)} sn</span>
              </div>
            ) : (
              <div style={{ overflowWrap: 'anywhere' }}>{l.text}</div>
            )}
          </div>
        ))}
      </div>
      {note && (
        <p className="error-text" role="alert" style={{ padding: '0 var(--s3)' }}>
          {note}
        </p>
      )}
      <div className="team-tools" role="toolbar" aria-label="Ekip sohbeti ekleri">
        <EmojiButton label="Emoji ekle" onPick={(em) => setText((v) => insertAt(inputRef.current, v, em))} />
        <button
          type="button"
          className="btn small ghost"
          disabled={offline}
          onClick={() => fileInput.current?.click()}
          aria-label="Dosya gönder"
          title="Dosya gönder (en fazla 10 MB)"
        >
          📎
        </button>
        <button
          type="button"
          className="btn small ghost"
          disabled={offline}
          onClick={() => (canUseLiveCamera() ? setCamera(true) : cameraInput.current?.click())}
          aria-label="Kamerayla fotoğraf çekip gönder"
          title="Kamerayla fotoğraf"
        >
          📷
        </button>
        <button
          type="button"
          className="btn small ghost"
          disabled={offline}
          onClick={() => galleryInput.current?.click()}
          aria-label="Galeriden fotoğraf gönder"
          title="Galeriden fotoğraf"
        >
          🖼️
        </button>
        {sending > 0 && (
          <span className="hint" role="status">
            Gönderiliyor…
          </span>
        )}
        <input
          ref={fileInput}
          type="file"
          hidden
          multiple
          data-testid="team-file-input"
          onChange={(e) => {
            pickFiles(e.target.files, false);
            e.target.value = '';
          }}
        />
        <input
          ref={galleryInput}
          type="file"
          hidden
          multiple
          accept="image/*"
          data-testid="team-gallery-input"
          onChange={(e) => {
            pickFiles(e.target.files, true);
            e.target.value = '';
          }}
        />
        <input
          ref={cameraInput}
          type="file"
          hidden
          accept="image/*"
          capture="environment"
          onChange={(e) => {
            pickFiles(e.target.files, true);
            e.target.value = '';
          }}
        />
      </div>
      <form className="team-form" onSubmit={submit}>
        <label htmlFor="team-input" className="sr-only">
          Ekibe mesaj (AI kapalı)
        </label>
        <input
          id="team-input"
          ref={inputRef}
          autoComplete="off"
          maxLength={REALTIME_POLICY.teamMaxChars}
          placeholder={conn === 'open' ? 'Ekibe yazın (AI kapalı)' : 'Bağlantı yok'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={offline}
        />
        <button className="btn" type="submit" disabled={offline || !text.trim()} aria-label="Ekibe gönder">
          Gönder
        </button>
      </form>
      {camera && <CameraCapture onCapture={(f) => void sendFile(f, true)} onClose={() => setCamera(false)} />}
      {viewing && (
        <div
          className="overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`Fotoğraf: ${viewing.name}`}
          onClick={() => setViewing(null)}
          onKeyDown={(e) => e.key === 'Escape' && setViewing(null)}
        >
          <div className="overlay-card photo" onClick={(e) => e.stopPropagation()}>
            <img
              src={viewing.url}
              alt={viewing.name}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
            <div className="row-actions">
              <button className="btn ghost" type="button" autoFocus onClick={() => setViewing(null)}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
